import { Helmet } from "react-helmet-async";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BASE_SITE_URL,
  buildLocalizedPublicUrl,
  buildPublicHreflangMap,
  buildPublicMarkdownUrl,
  extractBasePathFromCanonical,
  isIndexablePublicBasePath,
  isSupportedPublicLocale,
  stripLocaleFromPathname,
} from "@/config/publicSiteManifest.js";

interface MetaSEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogType?: "website" | "article";
  ogImage?: string;
  ogTitle?: string;
  ogDescription?: string;
  imageAlt?: string;
  twitterCard?: "summary" | "summary_large_image";
  schema?: object | object[];
}

const OG_LOCALE_MAP: Record<string, string> = {
  pt: "pt_PT",
  en: "en_US",
  fr: "fr_FR",
  es: "es_ES",
};

const PUBLIC_URL_KEYS = new Set(["url", "@id", "item"]);
const SCHEMA_TYPES_WITH_AUTO_LANGUAGE = new Set([
  "Article",
  "FAQPage",
  "WebApplication",
  "WebPage",
  "WebSite",
]);

const localizePublicSiteUrl = (value: string, locale: string) => {
  try {
    const parsed = new URL(value);
    if (parsed.origin !== BASE_SITE_URL) {
      return value;
    }

    const { basePath } = stripLocaleFromPathname(parsed.pathname || "/");
    if (!isIndexablePublicBasePath(basePath)) {
      return value;
    }

    return `${buildLocalizedPublicUrl(basePath, locale)}${parsed.search}${parsed.hash}`;
  } catch {
    return value;
  }
};

const schemaReferencesLocalizedPage = (value: unknown) => {
  if (typeof value !== "string") {
    return false;
  }

  try {
    const parsed = new URL(value);
    if (parsed.origin !== BASE_SITE_URL) {
      return false;
    }

    const { basePath } = stripLocaleFromPathname(parsed.pathname || "/");
    return isIndexablePublicBasePath(basePath);
  } catch {
    return false;
  }
};

const localizeSchemaNode = (node: unknown, locale: string): unknown => {
  if (Array.isArray(node)) {
    return node.map((item) => localizeSchemaNode(item, locale));
  }

  if (node && typeof node === "object") {
    const result: Record<string, unknown> = {};
    const schemaType = typeof (node as Record<string, unknown>)["@type"] === "string"
      ? (node as Record<string, unknown>)["@type"]
      : undefined;

    for (const [key, value] of Object.entries(node)) {
      if (key === "inLanguage" && typeof value === "string" && isSupportedPublicLocale(value)) {
        result[key] = locale;
        continue;
      }

      if (schemaType !== "Organization" && (key === "mainEntityOfPage" || PUBLIC_URL_KEYS.has(key)) && typeof value === "string") {
        result[key] = localizePublicSiteUrl(value, locale);
        continue;
      }

      result[key] = localizeSchemaNode(value, locale);
    }

    const localizedUrlCandidate =
      result.url ??
      result["@id"] ??
      result.item ??
      (typeof result.mainEntityOfPage === "string" ? result.mainEntityOfPage : undefined);

    if (
      schemaType &&
      SCHEMA_TYPES_WITH_AUTO_LANGUAGE.has(schemaType) &&
      typeof result.inLanguage === "undefined" &&
      schemaReferencesLocalizedPage(localizedUrlCandidate)
    ) {
      result.inLanguage = locale;
    }

    return result;
  }

  return node;
};

export const MetaSEO = ({
  title,
  description,
  canonical,
  ogType = "website",
  ogImage = "https://www.babete.pt/og-image.png",
  ogTitle,
  ogDescription,
  imageAlt,
  twitterCard = "summary_large_image",
  schema,
}: MetaSEOProps) => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const { locale: pathLocale, basePath } = stripLocaleFromPathname(location.pathname);

  const activeLocale =
    isIndexablePublicBasePath(basePath) && isSupportedPublicLocale(pathLocale)
      ? pathLocale
      : (isSupportedPublicLocale(i18n.resolvedLanguage || "") ? i18n.resolvedLanguage : "pt");

  const canonicalBasePath = extractBasePathFromCanonical(canonical || "") || null;
  const localizedCanonical =
    canonicalBasePath && isIndexablePublicBasePath(canonicalBasePath)
      ? buildLocalizedPublicUrl(canonicalBasePath, activeLocale)
      : canonical;

  const hreflangMap =
    canonicalBasePath && isIndexablePublicBasePath(canonicalBasePath)
      ? buildPublicHreflangMap(canonicalBasePath)
      : null;

  const localizedMarkdownUrl =
    canonicalBasePath && isIndexablePublicBasePath(canonicalBasePath)
      ? buildPublicMarkdownUrl(canonicalBasePath, activeLocale)
      : null;

  const fullTitle = title.includes("Babete") ? title : `${title} — Babete`;
  const ogTitleResolved = ogTitle || fullTitle;
  const ogDescriptionResolved = ogDescription || description;
  const imageAltResolved = imageAlt || fullTitle;

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Babete",
    url: BASE_SITE_URL,
    logo: `${BASE_SITE_URL}/logo.webp`,
    sameAs: ["https://twitter.com/babete", "https://linkedin.com/company/babete"],
    description:
      "Babete builds responsible AI platforms that turn complexity into clarity — covering Portuguese urban planning law, condominium legislation, agentic enterprise systems (Nucleus, Y-Brain, Laika Loop, LaikaHub, Parai), and public R&D labs (GenUI Lab, PYRA Lab, Loop Engineering Lab).",
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Babete Platforms",
      itemListElement: [
        {
          "@type": "SoftwareApplication",
          name: "Babete Urbanismo",
          url: "https://urbanismo.babete.pt",
          applicationCategory: "LegalService",
          offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
        },
        {
          "@type": "SoftwareApplication",
          name: "Babete Condomínios",
          url: "https://condominio.babete.pt",
          applicationCategory: "LegalService",
          offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
        },
        {
          "@type": "SoftwareApplication",
          name: "PME AI Toolkit",
          url: "https://pme.babete.pt",
          applicationCategory: "BusinessApplication",
          offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
        },
        {
          "@type": "SoftwareApplication",
          name: "Nucleus SME",
          url: "https://nucleus.babete.pt/sme",
          applicationCategory: "BusinessApplication",
          offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
        },
        {
          "@type": "SoftwareApplication",
          name: "Nucleus",
          url: "https://nucleus.babete.pt",
          applicationCategory: "BusinessApplication",
          offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
        },
        {
          "@type": "SoftwareApplication",
          name: "Y-Brain",
          url: "https://y-brain.babete.pt",
          applicationCategory: "BusinessApplication",
          offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
        },
        {
          "@type": "SoftwareApplication",
          name: "Laika Loop",
          url: "https://laikaloop.babete.pt",
          applicationCategory: "BusinessApplication",
          offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
        },
        {
          "@type": "SoftwareApplication",
          name: "GenUI Lab",
          url: "https://genui-lab.babete.pt",
          applicationCategory: "DeveloperApplication",
          offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
        },
        {
          "@type": "SoftwareApplication",
          name: "LaikaHub",
          url: "https://laikahub.babete.pt/b",
          applicationCategory: "BusinessApplication",
        },
        {
          "@type": "SoftwareApplication",
          name: "Parai",
          url: "https://parai.babete.pt",
          applicationCategory: "BusinessApplication",
        },
        {
          "@type": "SoftwareApplication",
          name: "PYRA Lab",
          url: "https://pyra-content-lab.babete.pt",
          applicationCategory: "DeveloperApplication",
          offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
        },
        {
          "@type": "SoftwareApplication",
          name: "Loop Engineering Lab",
          url: "https://loop-engineering-lab.babete.pt",
          applicationCategory: "DeveloperApplication",
          offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
        },
      ],
    },
  };

  const combinedSchema = schema
    ? Array.isArray(schema)
      ? [orgSchema, ...schema]
      : [orgSchema, schema]
    : [orgSchema];

  const localizedSchema = combinedSchema.map((entry) => localizeSchemaNode(entry, activeLocale));
  const ogLocale = OG_LOCALE_MAP[activeLocale] || OG_LOCALE_MAP.pt;

  useEffect(() => {
    if (i18n.resolvedLanguage !== activeLocale) {
      void i18n.changeLanguage(activeLocale);
    }
  }, [activeLocale, i18n]);

  return (
    <Helmet>
      <html lang={activeLocale} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {localizedCanonical && <link rel="canonical" href={localizedCanonical} />}
      {hreflangMap &&
        Object.entries(hreflangMap).map(([locale, href]) => (
          <link key={locale} rel="alternate" hrefLang={locale} href={href} />
        ))}
      {localizedMarkdownUrl && (
        <link
          rel="alternate"
          type="text/markdown"
          href={localizedMarkdownUrl}
          title={`${fullTitle} (Markdown)`}
        />
      )}

      <meta property="og:site_name" content="Babete" />
      <meta property="og:title" content={ogTitleResolved} />
      <meta property="og:description" content={ogDescriptionResolved} />
      <meta property="og:type" content={ogType} />
      {localizedCanonical && <meta property="og:url" content={localizedCanonical} />}
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={imageAltResolved} />
      <meta property="og:locale" content={ogLocale} />
      {hreflangMap &&
        Object.entries(OG_LOCALE_MAP)
          .filter(([locale]) => locale !== activeLocale)
          .map(([, localeCode]) => (
            <meta key={localeCode} property="og:locale:alternate" content={localeCode} />
          ))}

      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={ogTitleResolved} />
      <meta name="twitter:description" content={ogDescriptionResolved} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={imageAltResolved} />
      <meta name="twitter:site" content="@babete" />

      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />

      <script type="application/ld+json">{JSON.stringify(localizedSchema)}</script>
    </Helmet>
  );
};
